import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import Apiurl from "../environment/Apiurl";

// Blank form shape, kept as a factory so both the initial state and the
// "uncheck Case Retake" reset can build a fresh object from the same source.
const getBlankAddData = (type) => ({
  patient_id: "",
  registration_date: "",
  first_name: "",
  last_name: "",
  age: "",
  date_of_birth: "",
  gender: "",
  marital_status: "",
  spouse_name: "",
  mobile_no: "",
  email: "",
  address: "",
  city: "",
  state: "",
  pincode: "",
  id_proof_type: "",
  id_proof_no: "",
  notes: "",
  purpose: "",
  referred_by: "",
  referred_by_name: "",
  referred_by_mobile: "",
  patient_type: type || "", // Initialize patient_type with the passed type (e.g., "yoga")
});

function AddPatients() {
  const navigate = useNavigate();
  const location = useLocation();
  const { type } = location.state || {}; // Access type from location.state, default to empty object if undefined
  const [errors, setErrors] = useState({});
  const [basePatientId, setBasePatientId] = useState("");
  const [addData, setAddData] = useState(() => getBlankAddData(type));

  // Case Retake: create a brand-new patient record (own ID + own history)
  // for an existing patient, prefilled with their previous details.
  const [isCaseRetake, setIsCaseRetake] = useState(false);
  const [retakeSearchQuery, setRetakeSearchQuery] = useState("");
  const [retakeSearchStatus, setRetakeSearchStatus] = useState(""); // "", "loading", "found", "not_found", "error"
  const [retakeSourcePatientId, setRetakeSourcePatientId] = useState("");

  const [isregistered, setisregistered] = useState("No");
  const [registered_patients, setregistered_patients] = useState([]);
  const [selected_spouse, setselected_spouse] = useState("");
  const [responseStaffData, setResponseStaffData] = useState([]);
  const [responseDoctorData, setResponseDoctorData] = useState([]);
  const [referredByOptions, setReferredByOptions] = useState([]);
  const [errorMessage, setErrorMessage] = useState({});

  const isEditing = !!location.state?.patient;

  // Type-based patient ID generation
  const fetchGenerate = async () => {
    try {
      let endpoint = `${Apiurl}/generate_patient_id`; // Default for yoga
      if (addData.patient_type === "Homeopathy") {
        endpoint = `${Apiurl}/generate_homeo_id`;
      } else if (addData.patient_type === "Yoga") {
        // Use default endpoint for yoga
        endpoint = `${Apiurl}/generate_patient_id`;
      } // Add more types here if needed

      console.log(`Fetching patient ID from endpoint: ${endpoint}`);
      const response = await axios.get(endpoint);
      const generatedId = response.data.patient_id;
      console.log(`Generated patient ID: ${generatedId}`);
      setBasePatientId(generatedId);
      setAddData((prev) => ({
        ...prev,
        patient_id: generatedId,
      }));
    } catch (error) {
      console.error("Error fetching patient ID:", error.message);
      setErrors((prev) => ({
        ...prev,
        patient_id: "Failed to generate patient ID",
      }));
    }
  };

  const fetchpatient_data = async () => {
    try {
      const response = await axios.get(`${Apiurl}/generate_patient_id`);
      console.log("the response from the fetchpatient_data is : ", response);
    } catch (error) {
      console.error("Error fetching patient ID:", error.message);
    }
  };

  const fetchregistered_patients_data = async () => {
    try {
      const response = await axios.get(`${Apiurl}/patient_details`);
      console.log(
        "the response from the fetchregistered_patients_data is : ",
        response.data
      );
      setregistered_patients(response.data);
    } catch (error) {
      console.error("Error fetching patient ID:", error.message);
    }
  };

  const fetchStaffData = async () => {
    try {
      const response = await axios.get(`${Apiurl}/staff`);
      console.log(
        "the response from the setResponseStaffData is : ",
        response.data
      );
      setResponseStaffData(response.data);
    } catch (error) {
      console.error("Error fetching staff data:", error.message);
    }
  };

  const fetchDoctorData = async () => {
    try {
      const response = await axios.get(`${Apiurl}/doctor`);
      console.log(
        "the response from the setResponseDoctorData is : ",
        response.data
      );
      setResponseDoctorData(response.data);
    } catch (error) {
      console.error("Error fetching doctor data:", error.message);
    }
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    fetchpatient_data();
    fetchregistered_patients_data();
    fetchStaffData();
    fetchDoctorData();
  }, []);

  useEffect(() => {
    if (!isEditing) {
      fetchGenerate();
    }
  }, [isEditing]);

  // Update patient_type based on type from state
  useEffect(() => {
    if (isEditing && location.state?.patient) {
      const patient = location.state.patient;
      setAddData({
        ...addData,
        ...patient,
        patient_type: patient.patient_type || type || "",
      });
      setBasePatientId(patient.patient_id?.slice(0, -1));
    } else if (type) {
      // If not editing but type is provided, set patient_type
      setAddData((prev) => ({
        ...prev,
        patient_type: type,
      }));
    }
  }, [isEditing, location.state, type]);

  // Regenerate patient_id when patient_type changes (for non-editing mode).
  // Skipped during Case Retake so the retake ID (e.g. 4H-0003-R01) isn't
  // clobbered by a fresh sequential ID once the previous patient's
  // patient_type gets copied into the form.
  useEffect(() => {
    if (!isEditing && !isCaseRetake && addData.patient_type) {
      fetchGenerate();
    }
  }, [addData.patient_type, isEditing, isCaseRetake]);

  // Case Retake: toggle handler for the checkbox
  const handleCaseRetakeToggle = async (e) => {
    const checked = e.target.checked;
    setIsCaseRetake(checked);
    setRetakeSearchStatus("");
    setRetakeSearchQuery("");
    setRetakeSourcePatientId("");

    if (!checked) {
      // Reset to a normal, brand-new patient form + a freshly generated ID.
      setAddData(getBlankAddData(type));
      await fetchGenerate();
    }
  };

  // Case Retake: look up the existing patient by Patient ID or mobile
  // number, prefill the form with their previous details, and generate the
  // chained "-R01" / "-R02" ... retake ID.
  const handleFetchRetakeSource = async () => {
    const query = retakeSearchQuery.trim();
    if (!query) {
      setRetakeSearchStatus("error");
      return;
    }

    setRetakeSearchStatus("loading");
    try {
      const searchResponse = await axios.get(
        `${Apiurl}/patient_details/search`,
        { params: { query } }
      );
      const foundPatient = searchResponse.data;

      if (!foundPatient || !foundPatient.patient_id) {
        setRetakeSearchStatus("not_found");
        return;
      }

      // Drop identifiers/timestamps that must be fresh for the new record;
      // keep everything else (name, history, exam findings, etc.) as-is.
      const {
        _id,
        __v,
        patient_id: oldPatientId,
        created_by,
        created_date,
        updated_by,
        updated_date,
        ...previousDetails
      } = foundPatient;

      const idResponse = await axios.get(
        `${Apiurl}/generate_retake_id/${encodeURIComponent(oldPatientId)}`
      );
      const newRetakeId = idResponse.data.patient_id;

      setRetakeSourcePatientId(oldPatientId);
      setBasePatientId(newRetakeId);
      setAddData((prev) => ({
        ...prev,
        ...previousDetails,
        patient_id: newRetakeId,
      }));
      setRetakeSearchStatus("found");
    } catch (error) {
      console.error(
        "Case retake search error:",
        error.response?.data || error.message
      );
      setRetakeSearchStatus(
        error.response?.status === 404 ? "not_found" : "error"
      );
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "gender") {
      setAddData((prev) => ({
        ...prev,
        gender: value,
        patient_id:
          isregistered !== "Yes" ? `${basePatientId}` : prev.patient_id,
      }));
    } else if (name === "referred_by") {
      setAddData((prev) => ({
        ...prev,
        referred_by: value,
        referred_by_name: "",
        referred_by_mobile: "",
      }));
      setReferredByOptions(
        value === "Staff"
          ? responseStaffData
          : value === "Doctor"
          ? responseDoctorData
          : value === "Patient"
          ? registered_patients
          : []
      );
    } else {
      setAddData((prev) => ({ ...prev, [name]: value }));
    }

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
    if (errorMessage[name]) {
      setErrorMessage((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleReferredBySelection = (e) => {
    const value = e.target.value;
    if (addData.referred_by === "Staff") {
      const selectedStaff = responseStaffData.find(
        (staff) => staff.staff_id === value
      );
      if (selectedStaff) {
        setAddData((prev) => ({
          ...prev,
          referred_by_name: `${selectedStaff.first_name} ${selectedStaff.last_name}`,
          referred_by_mobile: selectedStaff.mobile_no,
        }));
      }
    } else if (addData.referred_by === "Doctor") {
      const selectedDoctor = responseDoctorData.find(
        (doctor) => doctor.doctor_id === value
      );
      if (selectedDoctor) {
        setAddData((prev) => ({
          ...prev,
          referred_by_name: selectedDoctor.doctor_name,
          referred_by_mobile: selectedDoctor.mobile_no,
        }));
      }
    } else if (addData.referred_by === "Patient") {
      const selectedPatient = registered_patients.find(
        (patient) => patient.patient_id === value
      );
      if (selectedPatient) {
        setAddData((prev) => ({
          ...prev,
          referred_by_name: `${selectedPatient.first_name} ${selectedPatient.last_name}`,
          referred_by_mobile: selectedPatient.mobile_no,
        }));
      }
    }
  };

  const handle_already_registered = (e) => {
    const value = e.target.value;
    setisregistered(value);
    if (value === "No") {
      setselected_spouse("");
      setAddData((prev) => ({
        ...prev,
        patient_id: basePatientId,
      }));
    }
  };

  const handle_select_spouse = (e) => {
    const value = e.target.value;
    setselected_spouse(value);

    if (value) {
      const added_value = value[value.length - 1] === "M" ? "F" : "M";
      const new_number = `${value.substring(
        0,
        value.length - 1
      )}${added_value}`;
      setAddData((prev) => ({
        ...prev,
        patient_id: new_number,
      }));
    } else {
      setAddData((prev) => ({
        ...prev,
        patient_id: basePatientId,
      }));
    }
  };

  const handleDateOfBirthChange = (e) => {
    const dob = e.target.value;
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }
    setAddData({
      ...addData,
      date_of_birth: dob,
      age: age.toString(),
    });
  };

  // const handleCancel = () => {
  //   navigate("/patients/yoga-patients");
  // };
  const handleCancel = () => {
    if (type === "Homeopathy") {
      navigate("/patients/homeopathy-patients");
    } else if (type === "Yoga") {
      navigate("/patients/yoga-patients");
    } else {
      navigate("/patients"); // fallback if type is unknown
    }
  };

  const handleSubmit = async () => {
    const newErrors = {};
    if (!addData.registration_date)
      newErrors.registration_date = "* Registration date is required";
    if (!addData.first_name) newErrors.first_name = "* First name is required";
    if (!addData.last_name) newErrors.last_name = "* Last name is required";

    if (!addData.gender) newErrors.gender = "* Gender is required";
    if (!addData.marital_status)
      newErrors.marital_status = "* Marital status is required";
    if (!addData.mobile_no) newErrors.mobile_no = "* Mobile number is required";

    if (Object.keys(newErrors).length > 0) {
      setErrorMessage(newErrors);
      return;
    }

    try {
      const formData = { ...addData };
      console.log("Submitting:", formData);

      const response = await axios.post(`${Apiurl}/patient_details`, formData, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      console.log("Response:", response.data);

      // Navigate based on patient_type
      if (formData.patient_type === "Yoga") {
        navigate("/patients/yoga-patients");
      } else if (formData.patient_type === "Homeopathy") {
        navigate("/patients/homeopathy-patients");
      } else {
        navigate("/patients");
      }
    } catch (error) {
      console.error("Submit Error:", error.response?.data || error.message);
    }
  };

  const getFieldClass = (fieldName) => {
    return `form-control ${
      errors[fieldName] || errorMessage[fieldName] ? "is-invalid" : ""
    }`;
  };

  const getSelectClass = (fieldName) => {
    return `form-select ${
      errors[fieldName] || errorMessage[fieldName] ? "is-invalid" : ""
    }`;
  };

  const renderStep1 = () => (
    <div className="row g-3">
      <div className="col-12">
        <div className="form-check">
          <input
            type="checkbox"
            className="form-check-input"
            id="case_retake_checkbox"
            checked={isCaseRetake}
            onChange={handleCaseRetakeToggle}
          />
          <label
            className="form-check-label fw-semibold"
            htmlFor="case_retake_checkbox"
          >
            Case Retake (new ID for an existing patient)
          </label>
        </div>
      </div>

      {isCaseRetake && (
        <div className="col-12 col-md-8">
          <label className="form-label fw-semibold">
            Existing Patient ID or Mobile Number
          </label>
          <div className="d-flex gap-2">
            <input
              type="text"
              className="form-control"
              placeholder="e.g. 4H-0003 or mobile number"
              value={retakeSearchQuery}
              onChange={(e) => setRetakeSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleFetchRetakeSource();
                }
              }}
            />
            <button
              type="button"
              className="btn btn-outline-primary text-nowrap"
              onClick={handleFetchRetakeSource}
              disabled={retakeSearchStatus === "loading"}
            >
              {retakeSearchStatus === "loading" ? "Searching..." : "Load Patient"}
            </button>
          </div>
          {retakeSearchStatus === "not_found" && (
            <div className="text-danger mt-1">
              No patient found for that ID / mobile number.
            </div>
          )}
          {retakeSearchStatus === "error" && (
            <div className="text-danger mt-1">
              Something went wrong. Please try again.
            </div>
          )}
          {retakeSearchStatus === "found" && retakeSourcePatientId && (
            <div className="text-success mt-1">
              Loaded details from <strong>{retakeSourcePatientId}</strong>.
              New Patient ID: <strong>{addData.patient_id}</strong>. Review
              and edit any fields below, then Submit.
            </div>
          )}
        </div>
      )}

      <div className="col-12 col-md-4">
        <label className="form-label fw-semibold">Patient ID</label>
        <input
          type="text"
          name="patient_id"
          className={getFieldClass("patient_id")}
          placeholder="Enter Patient ID"
          value={addData.patient_id}
          readOnly
        />
        {errors.patient_id && (
          <div className="invalid-feedback">{errors.patient_id}</div>
        )}
      </div>

      {/* Uncomment if you want to re-enable spouse selection */}
      {/* <div className="col-12 col-md-4">
        <label className="form-label fw-semibold">
          Already Registered Spouse
        </label>
        <select
          name="is_registered"
          className={getSelectClass("is_registered")}
          value={isregistered}
          onChange={handle_already_registered}
        >
          <option value="Yes">Yes</option>
          <option value="No">No</option>
        </select>
      </div>

      {isregistered === "Yes" && (
        <div className="col-12 col-md-4">
          <label className="form-label fw-semibold">Select Spouse</label>
          <select
            name="select_spouse"
            className={getSelectClass("select_spouse")}
            value={selected_spouse}
            onChange={handle_select_spouse}
          >
            <option value="">Select Spouse</option>
            {registered_patients.map((patient) => (
              <option key={patient.patient_id} value={patient.patient_id}>
                {patient.patient_id}-{patient.first_name}
              </option>
            ))}
          </select>
        </div>
      )} */}

      <div className="col-12 col-md-4">
        <label className="form-label fw-semibold">Registration Date</label>
        <input
          type="date"
          name="registration_date"
          className={getFieldClass("registration_date")}
          value={addData.registration_date}
          onChange={handleInputChange}
        />
        {errorMessage.registration_date && (
          <div className="text-danger" style={{ marginTop: "-15px" }}>
            {errorMessage.registration_date}
          </div>
        )}
      </div>

      <div className="col-12 col-md-4">
        <label className="form-label fw-semibold">First Name</label>
        <input
          type="text"
          name="first_name"
          className={getFieldClass("first_name")}
          placeholder="Enter First Name"
          value={addData.first_name}
          onChange={handleInputChange}
        />
        {errorMessage.first_name && (
          <div className="text-danger" style={{ marginTop: "-15px" }}>
            {errorMessage.first_name}
          </div>
        )}
      </div>

      <div className="col-12 col-md-4">
        <label className="form-label fw-semibold">Last Name</label>
        <input
          type="text"
          name="last_name"
          className={getFieldClass("last_name")}
          placeholder="Enter Last Name"
          value={addData.last_name}
          onChange={handleInputChange}
        />
        {errorMessage.last_name && (
          <div className="text-danger" style={{ marginTop: "-15px" }}>
            {errorMessage.last_name}
          </div>
        )}
      </div>

      <div className="col-12 col-md-4">
        <label className="form-label fw-semibold">Date of Birth</label>
        <input
          type="date"
          name="date_of_birth"
          className={getFieldClass("date_of_birth")}
          value={addData.date_of_birth}
          onChange={handleDateOfBirthChange}
        />
      </div>

      <div className="col-12 col-md-4">
        <label className="form-label fw-semibold">Age</label>
        <input
          type="number"
          name="age"
          className={getFieldClass("age")}
          placeholder="Enter Age"
          value={addData.age}
          readOnly
        />
      </div>

      <div className="col-12 col-md-4">
        <label className="form-label fw-semibold">Gender</label>
        <select
          name="gender"
          className={getSelectClass("gender")}
          value={addData.gender}
          onChange={handleInputChange}
        >
          <option value="">Select Gender</option>
          <option value="Male">Male</option>
          <option value="Female">Female</option>
          <option value="Other">Other</option>
        </select>
        {errorMessage.gender && (
          <div className="text-danger">{errorMessage.gender}</div>
        )}
      </div>

      <div className="col-12 col-md-4">
        <label className="form-label fw-semibold">Marital Status</label>
        <select
          name="marital_status"
          className={getSelectClass("marital_status")}
          value={addData.marital_status}
          onChange={handleInputChange}
        >
          <option value="">Select Marital Status</option>
          <option value="Single">Single</option>
          <option value="Married">Married</option>
          <option value="Divorced">Divorced</option>
          <option value="Widowed">Widowed</option>
        </select>
        {errorMessage.marital_status && (
          <div className="text-danger">{errorMessage.marital_status}</div>
        )}
      </div>

      <div className="col-12 col-md-4">
        <label className="form-label fw-semibold">
          Spouse Name / Father's Name
        </label>
        <input
          type="text"
          name="spouse_name"
          className={getFieldClass("spouse_name")}
          placeholder="Enter Spouse Name / Father's Name"
          value={addData.spouse_name}
          onChange={handleInputChange}
        />
      </div>

      <div className="col-12 col-md-4">
        <label className="form-label fw-semibold">Mobile Number</label>
        <input
          type="tel"
          name="mobile_no"
          className={getFieldClass("mobile_no")}
          placeholder="Enter Mobile Number"
          value={addData.mobile_no}
          onChange={handleInputChange}
          maxLength="10"
        />
        {errorMessage.mobile_no && (
          <div className="text-danger">{errorMessage.mobile_no}</div>
        )}
      </div>

      <div className="col-12 col-md-4">
        <label className="form-label fw-semibold">Email</label>
        <input
          type="email"
          name="email"
          className={getFieldClass("email")}
          placeholder="Enter Email"
          value={addData.email}
          onChange={handleInputChange}
        />
      </div>

      <div className="col-12 col-md-4">
        <label className="form-label fw-semibold">Address</label>
        <input
          type="text"
          name="address"
          className={getFieldClass("address")}
          placeholder="Enter Address"
          value={addData.address}
          onChange={handleInputChange}
        />
      </div>

      <div className="col-12 col-md-4">
        <label className="form-label fw-semibold">City</label>
        <input
          type="text"
          name="city"
          className={getFieldClass("city")}
          placeholder="Enter City"
          value={addData.city}
          onChange={handleInputChange}
        />
      </div>

      <div className="col-12 col-md-4">
        <label className="form-label fw-semibold">State</label>
        <select
          name="state"
          className={getSelectClass("state")}
          value={addData.state}
          onChange={handleInputChange}
        >
          <option value="">Select State</option>
          <option value="Andhra Pradesh">Andhra Pradesh</option>
          <option value="Arunachal Pradesh">Arunachal Pradesh</option>
          <option value="Assam">Assam</option>
          <option value="Bihar">Bihar</option>
          <option value="Chhattisgarh">Chhattisgarh</option>
          <option value="Goa">Goa</option>
          <option value="Gujarat">Gujarat</option>
          <option value="Haryana">Haryana</option>
          <option value="Himachal Pradesh">Himachal Pradesh</option>
          <option value="Jharkhand">Jharkhand</option>
          <option value="Karnataka">Karnataka</option>
          <option value="Kerala">Kerala</option>
          <option value="Madhya Pradesh">Madhya Pradesh</option>
          <option value="Maharashtra">Maharashtra</option>
          <option value="Manipur">Manipur</option>
          <option value="Meghalaya">Meghalaya</option>
          <option value="Mizoram">Mizoram</option>
          <option value="Nagaland">Nagaland</option>
          <option value="Odisha">Odisha</option>
          <option value="Punjab">Punjab</option>
          <option value="Rajasthan">Rajasthan</option>
          <option value="Sikkim">Sikkim</option>
          <option value="Tamil Nadu">Tamil Nadu</option>
          <option value="Telangana">Telangana</option>
          <option value="Tripura">Tripura</option>
          <option value="Uttar Pradesh">Uttar Pradesh</option>
          <option value="Uttarakhand">Uttarakhand</option>
          <option value="West Bengal">West Bengal</option>
          <option value="Delhi">Delhi</option>
          <option value="Jammu and Kashmir">Jammu and Kashmir</option>
          <option value="Ladakh">Ladakh</option>
          <option value="Puducherry">Puducherry</option>
          <option value="Other">Other</option>
        </select>
      </div>

      <div className="col-12 col-md-4">
        <label className="form-label fw-semibold">Pincode</label>
        <input
          type="text"
          name="pincode"
          className={getFieldClass("pincode")}
          placeholder="Enter Pincode"
          value={addData.pincode}
          onChange={handleInputChange}
          maxLength="6"
        />
      </div>

      <div className="col-12 col-md-4">
        <label className="form-label fw-semibold">ID Proof Type</label>
        <select
          name="id_proof_type"
          className={getSelectClass("id_proof_type")}
          value={addData.id_proof_type}
          onChange={handleInputChange}
        >
          <option value="">Select ID Proof Type</option>
          <option value="Aadhaar">Aadhaar</option>
          <option value="PAN">PAN</option>
          <option value="Voter ID">Voter ID</option>
          <option value="Driving License">Driving License</option>
          <option value="Passport">Passport</option>
        </select>
      </div>

      <div className="col-12 col-md-4">
        <label className="form-label fw-semibold">ID Proof Number</label>
        <input
          type="text"
          name="id_proof_no"
          className={getFieldClass("id_proof_no")}
          placeholder="Enter ID Proof Number"
          value={addData.id_proof_no}
          onChange={handleInputChange}
        />
      </div>

      <div className="col-12 col-md-4">
        <label className="form-label fw-semibold">Notes</label>
        <textarea
          name="notes"
          className={getFieldClass("notes")}
          placeholder="Enter Notes"
          rows="2"
          value={addData.notes}
          onChange={handleInputChange}
        ></textarea>
      </div>

      <div className="col-12 col-md-4">
        <label className="form-label fw-semibold">Purpose</label>
        <textarea
          name="purpose"
          className={getFieldClass("purpose")}
          placeholder="Enter Purpose"
          rows="2"
          value={addData.purpose}
          onChange={handleInputChange}
        ></textarea>
      </div>

      <div className="col-12 col-md-4">
        <label className="form-label fw-semibold">Source Type</label>
        <select
          name="referred_by"
          className={getSelectClass("referred_by")}
          value={addData.referred_by}
          onChange={handleInputChange}
        >
          <option value="">Select Source Type</option>
          <option value="Google">Google</option>
          <option value="Walk in">Walk in</option>
          <option value="Social Media">Social Media</option>
          <option value="Website">Website</option>
          <option value="Staff">Staff</option>
          <option value="Doctor">Doctor</option>
          <option value="Patient">Patient</option>
          <option value="Others">Others</option>
        </select>
      </div>

      {addData.referred_by === "Staff" ||
      addData.referred_by === "Doctor" ||
      addData.referred_by === "Patient" ? (
        <div className="col-12 col-md-4">
          <label className="form-label fw-semibold">Select Referred By</label>
          <select
            name="referred_by_select"
            className={getSelectClass("referred_by_select")}
            onChange={handleReferredBySelection}
          >
            <option value="">Select {addData.referred_by}</option>
            {referredByOptions.map((option) => (
              <option
                key={option.staff_id || option.doctor_id || option.patient_id}
                value={option.staff_id || option.doctor_id || option.patient_id}
              >
                {option.staff_id
                  ? `${option.staff_id} - ${option.first_name} ${option.last_name}`
                  : option.doctor_id
                  ? `${option.doctor_id} - ${option.doctor_name}`
                  : `${option.patient_id} - ${option.first_name} ${option.last_name}`}
              </option>
            ))}
          </select>
        </div>
      ) : null}

      <div className="col-12 col-md-4">
        <label className="form-label fw-semibold">Referred Name</label>
        <input
          type="text"
          name="referred_by_name"
          className={getFieldClass("referred_by_name")}
          placeholder="Enter Referred Name"
          value={addData.referred_by_name}
          onChange={handleInputChange}
          readOnly={
            addData.referred_by === "Staff" ||
            addData.referred_by === "Doctor" ||
            addData.referred_by === "Patient"
          }
        />
      </div>

      <div className="col-12 col-md-4">
        <label className="form-label fw-semibold">Referred Mobile</label>
        <input
          type="text"
          name="referred_by_mobile"
          className={getFieldClass("referred_by_mobile")}
          placeholder="Enter Referred Mobile"
          value={addData.referred_by_mobile}
          onChange={handleInputChange}
          readOnly={
            addData.referred_by === "Staff" ||
            addData.referred_by === "Doctor" ||
            addData.referred_by === "Patient"
          }
        />
      </div>

      <div className="col-12 col-md-4">
        <label className="form-label fw-semibold">Patient Type</label>
        <select
          name="patient_type"
          className={getSelectClass("patient_type")}
          value={addData.patient_type}
          onChange={handleInputChange}
          disabled={!!type} // Disable if type is passed (e.g., "yoga")
        >
          <option value="">Select Patient Type</option>
          <option value="Yoga">Yoga</option>
          <option value="Homeopathy">Homeopathy</option>
        </select>
      </div>

      <div className="text-center mt-4">
        <button
          type="button"
          className="btn btn-secondary me-3 px-4 py-2"
          onClick={handleCancel}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-info text-white fw-semibold px-4 py-2"
          style={{ backgroundColor: "#01AEBE" }}
          onClick={handleSubmit}
        >
          Submit
        </button>
      </div>
    </div>
  );

  return (
    <div className="p-2">
      <div className="card w-100 p-2">
        <div className="mb-3 text-center">
          <h2
            className="text-center text-white p-2"
            style={{ backgroundColor: "#01AEBE" }}
          >
            Basic Details
          </h2>
        </div>
        {renderStep1()}
      </div>
    </div>
  );
}

export default AddPatients;
